/**
 * Responses Component — Professional Chat UI
 * Full-featured conversation interface for viewing admin replies
 * and chatting back. Auto-scrolls, groups messages, shows status.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import './Responses.css';

const CATEGORY_LABELS = {
    feature: '✨ Feature',
    bug: '🐛 Bug',
    improvement: '🔧 Improvement',
    other: '💬 Other'
};

const STATUS_LABELS = {
    new: { label: 'Received', icon: '📩', cls: 'status-new' },
    read: { label: 'Reviewed', icon: '👁️', cls: 'status-read' },
    planned: { label: 'Planned', icon: '📋', cls: 'status-planned' },
    done: { label: 'Completed', icon: '✅', cls: 'status-done' },
    declined: { label: 'Declined', icon: '❌', cls: 'status-declined' }
};

const MAX_CHARS = 500;

const Responses = ({ username }) => {
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [replyText, setReplyText] = useState({});
    const [sending, setSending] = useState({});
    const [sentFlash, setSentFlash] = useState({});
    const threadRefs = useRef({});

    const fetchResponses = useCallback(async (silent = false) => {
        if (!username) return;
        if (!silent) setLoading(true);
        try {
            const data = await apiService.getUserResponses(username);
            setConversations(data.suggestions || []);
        } catch (error) {
            console.error('Failed to load responses:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [username]);

    useEffect(() => {
        fetchResponses();
        const interval = setInterval(() => fetchResponses(true), 10000);
        return () => clearInterval(interval);
    }, [fetchResponses]);

    // Auto-scroll to bottom when expanding or when messages change
    useEffect(() => {
        if (expandedId && threadRefs.current[expandedId]) {
            setTimeout(() => {
                const el = threadRefs.current[expandedId];
                if (el) el.scrollTop = el.scrollHeight;
            }, 100);
        }
    }, [expandedId, conversations]);

    const toggleExpand = (id) => {
        setExpandedId(prev => prev === id ? null : id);
    };

    const handleSendReply = async (suggestionId) => {
        const message = replyText[suggestionId]?.trim();
        if (!message || message.length > MAX_CHARS) return;

        setSending(prev => ({ ...prev, [suggestionId]: true }));
        try {
            const result = await apiService.sendReply(suggestionId, username, message);
            setConversations(prev => prev.map(conv => {
                if (conv.id === suggestionId) {
                    return { ...conv, replies: [...conv.replies, result.reply] };
                }
                return conv;
            }));
            setReplyText(prev => ({ ...prev, [suggestionId]: '' }));
            // Show sent confirmation
            setSentFlash(prev => ({ ...prev, [suggestionId]: true }));
            setTimeout(() => setSentFlash(prev => ({ ...prev, [suggestionId]: false })), 2000);
        } catch (error) {
            console.error('Failed to send reply:', error);
        } finally {
            setSending(prev => ({ ...prev, [suggestionId]: false }));
        }
    };

    // — Helpers —

    const formatTime = (ts) => {
        if (!ts) return '';
        const d = new Date(ts);
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const getDateLabel = (ts) => {
        if (!ts) return '';
        const d = new Date(ts);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const diff = Math.floor((today - msgDay) / 86400000);
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Yesterday';
        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    const timeAgo = (ts) => {
        if (!ts) return '';
        const diff = Date.now() - ts;
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7) return `${days}d ago`;
        return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const getLastMessage = (conv) => {
        if (!conv.replies.length) return null;
        return conv.replies[conv.replies.length - 1];
    };

    const isUnread = (conv) => {
        const lastAdmin = [...conv.replies].reverse().find(r => r.sender === 'admin');
        const lastUser = [...conv.replies].reverse().find(r => r.sender !== 'admin');
        return lastAdmin && (!lastUser || lastAdmin.created_at > lastUser.created_at);
    };

    // Group messages by date and consecutive sender
    const groupMessages = (replies) => {
        const groups = [];
        let currentDate = null;

        replies.forEach((reply, i) => {
            const dateLabel = getDateLabel(reply.created_at);
            if (dateLabel !== currentDate) {
                groups.push({ type: 'date', label: dateLabel });
                currentDate = dateLabel;
            }

            const prevReply = i > 0 ? replies[i - 1] : null;
            const sameAsPrev = prevReply &&
                prevReply.sender === reply.sender &&
                getDateLabel(prevReply.created_at) === dateLabel &&
                (reply.created_at - prevReply.created_at) < 300000; // 5 min gap

            groups.push({ type: 'message', reply, grouped: sameAsPrev });
        });

        return groups;
    };

    // — Render —

    if (loading) {
        return (
            <div className="responses-page">
                <div className="responses-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading conversations...</span>
                </div>
            </div>
        );
    }

    if (!conversations.length) {
        return (
            <div className="responses-page">
                <div className="responses-header">
                    <h1 className="responses-title">💬 Responses</h1>
                </div>
                <div className="responses-empty">
                    <div className="responses-empty-icon">📭</div>
                    <div className="responses-empty-text">No conversations yet</div>
                    <div className="responses-empty-subtext">
                        When the developer replies to your suggestions, conversations will appear here
                    </div>
                </div>
            </div>
        );
    }

    const unreadCount = conversations.filter(isUnread).length;

    return (
        <div className="responses-page">
            <div className="responses-header">
                <div>
                    <h1 className="responses-title">💬 Responses</h1>
                    <p className="responses-subtitle">
                        {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
                        {unreadCount > 0 && <span className="header-unread-pill">{unreadCount} new</span>}
                    </p>
                </div>
            </div>

            <div className="conversation-list">
                {conversations.map(conv => {
                    const expanded = expandedId === conv.id;
                    const unread = isUnread(conv);
                    const lastMsg = getLastMessage(conv);
                    const status = STATUS_LABELS[conv.status] || STATUS_LABELS.new;
                    const msgGroups = groupMessages(conv.replies);
                    const charCount = (replyText[conv.id] || '').length;

                    return (
                        <div key={conv.id} className={`conversation-card ${expanded ? 'card-expanded' : ''} ${unread ? 'card-unread' : ''}`}>
                            {/* Card Header */}
                            <div
                                className={`conversation-header ${expanded ? 'expanded' : ''}`}
                                onClick={() => toggleExpand(conv.id)}
                            >
                                <div className="conversation-header-left">
                                    <div className="conversation-title-row">
                                        {unread && <span className="unread-dot"></span>}
                                        <span className="conversation-title">{conv.title}</span>
                                    </div>
                                    <div className="conversation-meta">
                                        <span className={`conversation-badge category-${conv.category}`}>
                                            {CATEGORY_LABELS[conv.category] || conv.category}
                                        </span>
                                        <span className={`status-pill ${status.cls}`}>
                                            {status.icon} {status.label}
                                        </span>
                                        <span className="meta-separator">·</span>
                                        <span>{conv.replies.length} message{conv.replies.length !== 1 ? 's' : ''}</span>
                                    </div>
                                    {!expanded && lastMsg && (
                                        <div className="conversation-preview">
                                            <span className="preview-sender">
                                                {lastMsg.sender === 'admin' ? '🛡️ Dev' : '👤 You'}:
                                            </span>
                                            <span className="preview-text">{lastMsg.message}</span>
                                            <span className="preview-time">{timeAgo(lastMsg.created_at)}</span>
                                        </div>
                                    )}
                                </div>
                                <span className={`conversation-chevron ${expanded ? 'expanded' : ''}`}>
                                    ‹
                                </span>
                            </div>

                            {/* Expanded Thread */}
                            <div className={`conversation-thread ${expanded ? 'expanded' : ''}`}>
                                {/* Original Suggestion */}
                                <div className="thread-original">
                                    <div className="thread-original-header">
                                        <span className="thread-original-label">📝 Your suggestion</span>
                                        <span className="thread-original-date">
                                            {new Date(conv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </span>
                                    </div>
                                    <div className="thread-original-text">{conv.description}</div>
                                </div>

                                {/* Messages */}
                                <div
                                    className="thread-messages"
                                    ref={el => threadRefs.current[conv.id] = el}
                                >
                                    {msgGroups.map((item, idx) => {
                                        if (item.type === 'date') {
                                            return (
                                                <div key={`date-${idx}`} className="date-separator">
                                                    <span>{item.label}</span>
                                                </div>
                                            );
                                        }

                                        const { reply, grouped } = item;
                                        const isAdmin = reply.sender === 'admin';

                                        return (
                                            <div
                                                key={reply.id}
                                                className={`thread-message ${isAdmin ? 'admin' : 'user'} ${grouped ? 'grouped' : ''}`}
                                            >
                                                {!grouped && (
                                                    <div className="thread-message-sender">
                                                        {isAdmin ? '🛡️ Developer' : `👤 ${reply.sender}`}
                                                    </div>
                                                )}
                                                <div className="thread-message-bubble">
                                                    <div className="thread-message-text">{reply.message}</div>
                                                    <div className="thread-message-time">{formatTime(reply.created_at)}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Reply Bar */}
                                <div className="thread-reply-bar">
                                    {sentFlash[conv.id] && (
                                        <div className="sent-confirmation">✓ Message sent</div>
                                    )}
                                    <div className="thread-reply-row">
                                        <textarea
                                            className="thread-reply-input"
                                            placeholder="Write a reply..."
                                            value={replyText[conv.id] || ''}
                                            onChange={(e) => {
                                                if (e.target.value.length <= MAX_CHARS) {
                                                    setReplyText(prev => ({ ...prev, [conv.id]: e.target.value }));
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendReply(conv.id);
                                                }
                                            }}
                                            rows={1}
                                        />
                                        <button
                                            className="thread-reply-btn"
                                            onClick={() => handleSendReply(conv.id)}
                                            disabled={!replyText[conv.id]?.trim() || sending[conv.id] || charCount > MAX_CHARS}
                                            title="Press Enter to send"
                                        >
                                            {sending[conv.id] ? (
                                                <span className="btn-loading"></span>
                                            ) : '↵'}
                                        </button>
                                    </div>
                                    <div className="reply-footer">
                                        <span className="reply-hint">Shift+Enter for new line</span>
                                        <span className={`char-count ${charCount > MAX_CHARS * 0.9 ? 'warn' : ''} ${charCount > MAX_CHARS ? 'over' : ''}`}>
                                            {charCount}/{MAX_CHARS}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Responses;
